import { gsap } from 'gsap'
import { ResizeCallback } from '../utils/resize-callback.js'
/**
 * Marquee Component
 *
 * Expected CSS structure (based on actual HTML):
 *
 * .component--logos {
 *   overflow: clip;
 * }
 *
 * .marquee {
 *   // marquee container
 * }
 *
 * .marquee__track {
 *   --gap: 50px;
 *   display: flex;
 *   white-space: nowrap;
 *   flex-direction: row;
 * }
 *
 * .marquee__row {
 *   display: flex;
 *   white-space: nowrap;
 *   gap: var(--gap);
 *   flex: 0 0 auto;
 *   width: auto;
 *   padding-right: var(--gap);
 * }
 *
 * .logos__item {
 *   position: relative;
 *   flex: 0 0 auto;
 *   width: auto;
 *
 *   img {
 *     display: block;
 *     height: 13vw;
 *   }
 *
 *   a {
 *     position: absolute;
 *     inset: 0;
 *   }
 *
 *   &:has(a):hover {
 *     opacity: 0.7;
 *   }
 * }
 *
 * Required HTML structure:
 * <div class="component--logos js-logos">
 *   <div class="logos marquee js-marquee">
 *     <div class="logos__wrapper marquee__track js-marquee-track">
 *       <div class="logos__inner marquee__row js-marquee-row">
 *         <div class="logos__item">
 *           <img src="..." />
 *           <a href="..." class="logos__item__link"></a>
 *         </div>
 *       </div>
 *     </div>
 *   </div>
 * </div>
 */
export class Marquee {
	constructor(el, options = {}) {
		this.el = el

		this.options = {
			speed: 60, // px/sec
			pauseOnHover: true, // enable hover pause
			...options,
		}

		this.track = this.el.querySelector('.js-marquee-track')
		this.row = this.el.querySelector('.js-marquee-row')

		this.clones = []
		this.tween = null

		this.setup()
		this.createTimeline()
		this.bindEvents()
	}

	// ------------------------------------------------------------ //
	// SETUP
	// ------------------------------------------------------------ //
	setup() {
		// make logos clickable
		this.track.style.pointerEvents = 'auto'

		// scroll container
		this.track.style.display = 'flex'
		this.track.style.whiteSpace = 'nowrap'

		this.recalculate()
	}

	recalculate() {
		this.rowWidth = this.row.offsetWidth
		if (!this.rowWidth) return

		// Clone the row enough times to fill the viewport plus one extra
		// copy, so the loop stays seamless no matter how narrow the row is.
		const viewportWidth = this.el.offsetWidth
		const copiesNeeded = Math.ceil(viewportWidth / this.rowWidth) + 1
		const clonesNeeded = Math.max(1, copiesNeeded - 1) // original counts as one

		// Add missing clones (avoid rebuilding on every resize).
		while (this.clones.length < clonesNeeded) {
			const clone = this.row.cloneNode(true)
			clone.setAttribute('aria-hidden', 'true')
			this.track.appendChild(clone)
			this.clones.push(clone)
		}
		// Remove any surplus clones (e.g. after the viewport grows narrower).
		while (this.clones.length > clonesNeeded) {
			const clone = this.clones.pop()
			clone.remove()
		}

		this.duration = this.rowWidth / this.options.speed // seconds
	}

	// ------------------------------------------------------------ //
	// GSAP
	// ------------------------------------------------------------ //
	createTimeline() {
		if (this.tween) this.tween.kill()

		this.tween = gsap.to(this.track, {
			x: -this.rowWidth,
			duration: this.duration,
			ease: 'none',
			repeat: -1,
			modifiers: {
				x: gsap.utils.unitize((v) => parseFloat(v) % -this.rowWidth),
			},
		})
	}

	// ------------------------------------------------------------ //
	// EVENTS
	// ------------------------------------------------------------ //
	bindEvents() {
		// Use ResizeCallback instead of direct resize listener
		this.resizeCallback = new ResizeCallback(
			(data) => {
				// Only recalculate if width changed (x-axis)
				this.recalculate()
				this.createTimeline()
			},
			{
				delay: 150, // slightly longer delay for performance
				resizeThreshold: 10, // only trigger if width changes by at least 10px
				detectAxis: ['x'], // only detect horizontal changes
			},
		)

		if (this.options.pauseOnHover) {
			this.el.addEventListener('mouseenter', this.pause)
			this.el.addEventListener('mouseleave', this.resume)
		}
	}

	pause = () => {
		if (!this.tween) return

		gsap.to(this.tween, {
			timeScale: 0,
			duration: 1, // how long the slowdown takes
			ease: 'power2.out',
		})
	}

	resume = () => {
		if (!this.tween) return

		gsap.to(this.tween, {
			timeScale: 1,
			duration: 1, // speed-up duration
			ease: 'power2.out',
		})
	}

	// ------------------------------------------------------------ //
	// CLEANUP
	// ------------------------------------------------------------ //
	destroy() {
		// Clean up ResizeCallback instead of direct event listener
		if (this.resizeCallback) {
			this.resizeCallback.destroy()
			this.resizeCallback = null
		}

		if (this.options.pauseOnHover) {
			this.el.removeEventListener('mouseenter', this.pause)
			this.el.removeEventListener('mouseleave', this.resume)
		}

		if (this.tween) this.tween.kill()

		// Remove clones so a re-init starts from a single row.
		if (this.clones) {
			this.clones.forEach((clone) => clone.remove())
			this.clones = []
		}
	}
}
